/**
 * File: assets/basari-cart.js
 * Purpose: Cart Drawer Foundation — Lifecycle / Controller / Service / Renderer
 * Sprint: 1B.15A V2 — Stabilization Pass
 * Owner: Basari Theme / Cart
 * Dependencies: Dawn <cart-drawer>, Shopify AJAX Cart + section rendering,
 *   window.routes, PUB_SUB_EVENTS / subscribe (optional), [data-basari-cart]
 *
 * Architecture:
 *   BasariCartLifecycle → BasariCartController → BasariCartService → BasariCartRenderer
 *
 * Ownership:
 *   Dawn   — drawer open/close, overlay, Escape, focus trap, section HTML, shell markup
 *   Basari — presentation updates from Liquid sections, qty/remove AJAX, loading/errors
 *
 * Rendering strategy (single source of truth):
 *   Liquid renders cart markup. JS applies returned cart-drawer section HTML.
 *   JS does not build item HTML.
 *
 * Required theme setting: Cart type = Drawer (do not commit settings_data.json).
 */
(function (global) {
  'use strict';

  var ROOT_SELECTOR = '[data-basari-cart]';
  var HOST_SELECTOR = 'cart-drawer[data-basari-cart-host]';
  var DRAWER_SELECTOR = 'cart-drawer';
  var READY_ATTR = 'data-basari-cart-ready';
  var LOADING_CLASS = 'basari-cart-host--loading';
  var EMPTY_CLASS = 'is-empty';
  var SECTION_CART_DRAWER = 'cart-drawer';
  var SECTION_CART_ICON = 'cart-icon-bubble';

  var EVENTS = {
    INITIALIZED: 'basari:cart:initialized',
    OPENED: 'basari:cart:opened',
    CLOSED: 'basari:cart:closed',
    UPDATED: 'basari:cart:updated',
    ERROR: 'basari:cart:error',
    LOADING: 'basari:cart:loading'
  };

  /* ---------------------------------------------------------------------------
   * Utilities
   * ------------------------------------------------------------------------- */

  function emit(name, detail) {
    try {
      document.dispatchEvent(
        new CustomEvent(name, {
          detail: detail || {},
          bubbles: true
        })
      );
    } catch (error) {
      /* CustomEvent failures must not break cart flow. */
    }
  }

  function getRoutes() {
    return global.routes || {};
  }

  function parseJsonResponse(response) {
    return response.json().then(function (payload) {
      if (!response.ok) {
        var message =
          (payload && (payload.description || payload.message)) ||
          (global.cartStrings && global.cartStrings.error) ||
          'Unable to update cart.';
        var error = new Error(message);
        error.payload = payload;
        error.status = response.status;
        throw error;
      }
      return payload;
    });
  }

  function fetchConfigJson() {
    if (typeof global.fetchConfig === 'function') {
      return global.fetchConfig('json');
    }
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      }
    };
  }

  function getSectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /* ---------------------------------------------------------------------------
   * BasariCartService — Shopify AJAX Cart + section HTML only (no DOM)
   * ------------------------------------------------------------------------- */

  function BasariCartService() {}

  BasariCartService.prototype.getCart = function () {
    var routes = getRoutes();
    var url = (routes.cart_url || '/cart') + '.js';

    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(parseJsonResponse);
  };

  /**
   * Fetch Liquid-rendered cart-drawer section (primary UI source of truth).
   */
  BasariCartService.prototype.getDrawerSection = function () {
    var routes = getRoutes();
    var url = (routes.cart_url || '/cart') + '?section_id=' + SECTION_CART_DRAWER;

    return fetch(url, {
      method: 'GET',
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) {
        throw new Error((global.cartStrings && global.cartStrings.error) || 'Unable to load cart.');
      }
      return response.text();
    });
  };

  /**
   * Change a line quantity. quantity 0 removes the line.
   * Requests Liquid sections so Renderer can apply Dawn/Basari markup.
   */
  BasariCartService.prototype.changeLine = function (options) {
    options = options || {};
    var routes = getRoutes();
    var url = routes.cart_change_url || '/cart/change.js';
    var sections = options.sections || [SECTION_CART_DRAWER, SECTION_CART_ICON];
    var body = {
      quantity: Number(options.quantity) || 0,
      sections: sections,
      sections_url: options.sectionsUrl || global.location.pathname
    };

    if (options.line) {
      body.line = Number(options.line);
    } else if (options.key) {
      body.id = options.key;
    }

    var config = fetchConfigJson();
    config.method = 'POST';
    config.body = JSON.stringify(body);
    config.credentials = 'same-origin';

    return fetch(url, config).then(parseJsonResponse).then(function (payload) {
      if (payload && payload.errors) {
        var message =
          typeof payload.errors === 'string'
            ? payload.errors
            : (global.cartStrings && global.cartStrings.error) || 'Unable to update cart.';
        var error = new Error(message);
        error.payload = payload;
        throw error;
      }
      return payload;
    });
  };

  /* ---------------------------------------------------------------------------
   * BasariCartRenderer — DOM only; applies Liquid section HTML (no item builder)
   * ------------------------------------------------------------------------- */

  function BasariCartRenderer(root) {
    this.root = root;
    this.host = (root && root.closest(HOST_SELECTOR)) || document.querySelector(HOST_SELECTOR);
    this._cacheElements();
  }

  BasariCartRenderer.prototype._cacheElements = function () {
    var root = this.root;
    var host = this.host;
    this.itemsEl = root ? root.querySelector('[data-basari-cart-items]') : null;
    this.emptyEl = root ? root.querySelector('[data-basari-cart-empty]') : null;
    this.summaryEl = root ? root.querySelector('[data-basari-cart-summary]') : null;
    this.errorsEl = root ? root.querySelector('[data-basari-cart-errors]') : null;
    this.statusEl = root ? root.querySelector('[data-basari-cart-status]') : null;
    this.checkoutBtn = root
      ? root.querySelector('[data-basari-cart-checkout], #CartDrawer-Checkout')
      : null;
    this.loadingEl = host
      ? host.querySelector('[data-basari-cart-loading]')
      : document.querySelector('[data-basari-cart-loading]');
    this.currency = root ? root.getAttribute('data-basari-cart-currency') || '' : '';
    this.loadingLabel =
      (root && root.getAttribute('data-basari-cart-string-loading')) ||
      (global.cartStrings && global.cartStrings.error) ||
      'Loading';
  };

  BasariCartRenderer.prototype.rebind = function (root) {
    this.root = root;
    this.host = (root && root.closest(HOST_SELECTOR)) || document.querySelector(HOST_SELECTOR);
    this._cacheElements();
  };

  BasariCartRenderer.prototype.setLoading = function (isLoading) {
    if (this.host) {
      this.host.classList.toggle(LOADING_CLASS, !!isLoading);
    }
    if (this.loadingEl) {
      if (isLoading) {
        this.loadingEl.removeAttribute('hidden');
        this.loadingEl.setAttribute('aria-busy', 'true');
        this.loadingEl.setAttribute('aria-hidden', 'false');
      } else {
        this.loadingEl.setAttribute('hidden', '');
        this.loadingEl.setAttribute('aria-busy', 'false');
        this.loadingEl.setAttribute('aria-hidden', 'true');
      }
    }
    if (this.statusEl && isLoading) {
      this.statusEl.textContent = this.loadingLabel;
    }
  };

  BasariCartRenderer.prototype.showError = function (message) {
    if (!this.errorsEl) return;
    var text = message || (global.cartStrings && global.cartStrings.error) || 'Unable to update cart.';
    this.errorsEl.textContent = text;
    this.errorsEl.removeAttribute('hidden');
    if (this.statusEl) this.statusEl.textContent = text;
  };

  BasariCartRenderer.prototype.clearError = function () {
    if (!this.errorsEl) return;
    this.errorsEl.textContent = '';
    this.errorsEl.setAttribute('hidden', '');
  };

  BasariCartRenderer.prototype.setEmptyState = function (isEmpty) {
    var drawer = this.host || (this.root && this.root.closest(DRAWER_SELECTOR));
    if (drawer) drawer.classList.toggle(EMPTY_CLASS, !!isEmpty);

    var itemsHost = this.root && this.root.querySelector('cart-drawer-items');
    if (itemsHost) itemsHost.classList.toggle(EMPTY_CLASS, !!isEmpty);

    if (this.checkoutBtn) {
      if (isEmpty) this.checkoutBtn.setAttribute('disabled', '');
      else this.checkoutBtn.removeAttribute('disabled');
    }
  };

  /**
   * Apply Liquid cart-drawer section HTML into Dawn's #CartDrawer shell.
   * This is the only full UI refresh path — no JS item construction.
   */
  BasariCartRenderer.prototype.applyDrawerSection = function (sectionHtml) {
    if (!sectionHtml) return false;
    var cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return false;

    var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    var sourceDrawer = doc.querySelector('#CartDrawer');
    if (!sourceDrawer) return false;

    cartDrawer.innerHTML = sourceDrawer.innerHTML;

    var drawer = cartDrawer.closest(DRAWER_SELECTOR);
    var sourceHost = doc.querySelector('cart-drawer');
    if (drawer && sourceHost) {
      drawer.classList.toggle(EMPTY_CLASS, sourceHost.classList.contains(EMPTY_CLASS));
    }

    /* Re-bind overlay close — Dawn renderContents does the same after section swap. */
    var overlay = cartDrawer.querySelector('#CartDrawer-Overlay');
    if (drawer && overlay && typeof drawer.close === 'function') {
      overlay.addEventListener('click', drawer.close.bind(drawer));
    }

    var root = cartDrawer.querySelector(ROOT_SELECTOR);
    this.rebind(root);

    var itemCount = root ? root.querySelectorAll('[data-basari-cart-item]').length : 0;
    this.setEmptyState(itemCount === 0);
    this.clearError();
    if (this.statusEl) this.statusEl.textContent = 'Cart updated';
    return true;
  };

  BasariCartRenderer.prototype.updateCartIconBubble = function (sectionHtml) {
    if (!sectionHtml) return;
    var currentIcon = document.getElementById('cart-icon-bubble');
    if (!currentIcon) return;

    var html = getSectionInnerHTML(sectionHtml, '.shopify-section');
    if (!html) {
      var doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      html = doc.body ? doc.body.innerHTML : sectionHtml;
    }
    currentIcon.innerHTML = html;
  };

  BasariCartRenderer.prototype.applyCartResponse = function (cart) {
    if (!cart || !cart.sections) return false;
    var drawerHtml = cart.sections[SECTION_CART_DRAWER];
    var applied = this.applyDrawerSection(drawerHtml);
    if (cart.sections[SECTION_CART_ICON]) {
      this.updateCartIconBubble(cart.sections[SECTION_CART_ICON]);
    }
    if (cart.item_count != null) {
      this.setEmptyState(Number(cart.item_count) === 0);
    }
    return applied;
  };

  /* ---------------------------------------------------------------------------
   * BasariCartLifecycle — mount / remount / Dawn section-swap observation
   * ------------------------------------------------------------------------- */

  function BasariCartLifecycle(options) {
    this._mount = options && options.mount;
    this._onOpen = options && options.onOpen;
    this._onClose = options && options.onClose;
    this._swapObserver = null;
    this._openObserver = null;
    this._sectionLoadHandler = null;
  }

  BasariCartLifecycle.prototype.start = function () {
    var self = this;
    if (typeof this._mount === 'function') this._mount();

    this._observeSectionSwap();
    this._observeOpenClose();

    this._sectionLoadHandler = function () {
      if (typeof self._mount === 'function') self._mount();
      self._observeSectionSwap();
      self._observeOpenClose();
    };
    document.addEventListener('shopify:section:load', this._sectionLoadHandler);
  };

  BasariCartLifecycle.prototype.stop = function () {
    if (this._swapObserver) {
      this._swapObserver.disconnect();
      this._swapObserver = null;
    }
    if (this._openObserver) {
      this._openObserver.disconnect();
      this._openObserver = null;
    }
    if (this._sectionLoadHandler) {
      document.removeEventListener('shopify:section:load', this._sectionLoadHandler);
      this._sectionLoadHandler = null;
    }
  };

  BasariCartLifecycle.prototype.remount = function () {
    if (typeof this._mount === 'function') return this._mount();
    return null;
  };

  BasariCartLifecycle.prototype._observeSectionSwap = function () {
    var shell = document.getElementById('CartDrawer');
    var self = this;
    if (!shell || typeof MutationObserver === 'undefined') return;

    if (this._swapObserver) this._swapObserver.disconnect();

    this._swapObserver = new MutationObserver(function () {
      if (typeof self._mount === 'function') self._mount();
    });
    this._swapObserver.observe(shell, { childList: true });
  };

  BasariCartLifecycle.prototype._observeOpenClose = function () {
    var host = document.querySelector(HOST_SELECTOR) || document.querySelector(DRAWER_SELECTOR);
    var self = this;
    if (!host || typeof MutationObserver === 'undefined') return;

    if (this._openObserver) this._openObserver.disconnect();

    var wasActive = host.classList.contains('active');
    this._openObserver = new MutationObserver(function () {
      var isActive = host.classList.contains('active');
      if (isActive && !wasActive && typeof self._onOpen === 'function') {
        self._onOpen(host);
      } else if (!isActive && wasActive && typeof self._onClose === 'function') {
        self._onClose(host);
      }
      wasActive = isActive;
    });
    this._openObserver.observe(host, { attributes: true, attributeFilter: ['class'] });
  };

  /* ---------------------------------------------------------------------------
   * BasariCartController — orchestration only
   * ------------------------------------------------------------------------- */

  function BasariCartController(root, service, renderer) {
    this.root = root;
    this.service = service || new BasariCartService();
    this.renderer = renderer || new BasariCartRenderer(root);
    this._boundClick = this._onClick.bind(this);
    this._boundKeydown = this._onKeydown.bind(this);
    this._cartUpdateUnsubscriber = null;
    this._busy = false;
    this._lastFocus = null;
  }

  BasariCartController.prototype.init = function () {
    if (!this.root || this.root.getAttribute(READY_ATTR) === 'true') return this;

    this.root.setAttribute(READY_ATTR, 'true');
    this.root.addEventListener('click', this._boundClick);
    this.root.addEventListener('keydown', this._boundKeydown);
    this._subscribeCartUpdates();

    emit(EVENTS.INITIALIZED, { root: this.root });
    return this;
  };

  BasariCartController.prototype.destroy = function () {
    if (!this.root) return;
    this.root.removeEventListener('click', this._boundClick);
    this.root.removeEventListener('keydown', this._boundKeydown);
    this.root.removeAttribute(READY_ATTR);
    if (typeof this._cartUpdateUnsubscriber === 'function') {
      this._cartUpdateUnsubscriber();
      this._cartUpdateUnsubscriber = null;
    }
  };

  BasariCartController.prototype.getDrawer = function () {
    return (
      document.querySelector(HOST_SELECTOR) ||
      (this.root && this.root.closest(DRAWER_SELECTOR)) ||
      document.querySelector(DRAWER_SELECTOR)
    );
  };

  /**
   * Compose with Dawn open — never override CartDrawer.prototype.
   */
  BasariCartController.prototype.open = function (triggeredBy) {
    var drawer = this.getDrawer();
    this._lastFocus = triggeredBy || document.activeElement;
    if (drawer && typeof drawer.open === 'function') {
      if (this._lastFocus && typeof drawer.setActiveElement === 'function') {
        drawer.setActiveElement(this._lastFocus);
      }
      drawer.open(this._lastFocus);
    }
    emit(EVENTS.OPENED, { drawer: drawer });
    return this.refresh();
  };

  BasariCartController.prototype.close = function () {
    var drawer = this.getDrawer();
    if (drawer && typeof drawer.close === 'function') {
      drawer.close();
    }
    emit(EVENTS.CLOSED, { drawer: drawer });
  };

  /**
   * Refresh UI from Liquid cart-drawer section (not from JS-built markup).
   */
  BasariCartController.prototype.refresh = function () {
    var self = this;
    if (this._busy) return Promise.resolve(null);

    this._busy = true;
    this.renderer.setLoading(true);
    emit(EVENTS.LOADING, { loading: true });

    return this.service
      .getDrawerSection()
      .then(function (sectionHtml) {
        self.renderer.applyDrawerSection(sectionHtml);
        if (lifecycle) lifecycle.remount();
        emit(EVENTS.UPDATED, { source: 'refresh' });
        return sectionHtml;
      })
      .catch(function (error) {
        self.renderer.showError(error && error.message);
        emit(EVENTS.ERROR, { error: error });
        return null;
      })
      .then(function (result) {
        self.renderer.setLoading(false);
        emit(EVENTS.LOADING, { loading: false });
        self._busy = false;
        return result;
      });
  };

  BasariCartController.prototype._subscribeCartUpdates = function () {
    var self = this;
    if (typeof global.subscribe === 'function' && global.PUB_SUB_EVENTS && global.PUB_SUB_EVENTS.cartUpdate) {
      this._cartUpdateUnsubscriber = global.subscribe(global.PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === 'basari-cart') return;
        /* Dawn product-form already calls renderContents with cart-drawer section.
           Remount only — avoid a redundant section fetch when sections are present. */
        setTimeout(function () {
          if (lifecycle) lifecycle.remount();
          var cart = event && event.cartData;
          if (cart && cart.sections && cart.sections[SECTION_CART_DRAWER]) {
            return;
          }
          if (activeController) activeController.refresh();
        }, 0);
      });
    }
  };

  BasariCartController.prototype._onClick = function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var removeBtn = target.closest('[data-basari-cart-remove]');
    if (removeBtn) {
      event.preventDefault();
      this._changeQuantity(removeBtn, 0);
      return;
    }

    var decreaseBtn = target.closest('[data-basari-cart-qty-decrease]');
    if (decreaseBtn) {
      event.preventDefault();
      var itemEl = decreaseBtn.closest('[data-basari-cart-item]');
      this._changeQuantity(decreaseBtn, Math.max(0, this._readQuantity(itemEl) - 1));
      return;
    }

    var increaseBtn = target.closest('[data-basari-cart-qty-increase]');
    if (increaseBtn) {
      event.preventDefault();
      var itemIncrease = increaseBtn.closest('[data-basari-cart-item]');
      this._changeQuantity(increaseBtn, this._readQuantity(itemIncrease) + 1);
      return;
    }

    var continueBtn = target.closest('[data-basari-cart-continue]');
    if (continueBtn) {
      this.close();
    }
  };

  BasariCartController.prototype._onKeydown = function (event) {
    if (!event) return;
    var target = event.target;
    if (!target || !target.closest) return;

    if (event.key === 'Enter' || event.key === ' ') {
      var qtyBtn = target.closest(
        '[data-basari-cart-qty-decrease], [data-basari-cart-qty-increase], [data-basari-cart-remove]'
      );
      if (qtyBtn) {
        event.preventDefault();
        qtyBtn.click();
      }
    }
  };

  BasariCartController.prototype._readQuantity = function (itemEl) {
    if (!itemEl) return 0;
    var valueEl = itemEl.querySelector('[data-basari-cart-qty-value]');
    var parsed = parseInt(valueEl && valueEl.textContent, 10);
    return isNaN(parsed) ? 0 : parsed;
  };

  BasariCartController.prototype._changeQuantity = function (trigger, quantity) {
    var self = this;
    if (!trigger || this._busy) return Promise.resolve(null);

    var line = parseInt(trigger.getAttribute('data-basari-cart-line'), 10);
    var key = trigger.getAttribute('data-basari-cart-key');
    if (!line && !key) return Promise.resolve(null);

    this._busy = true;
    this.renderer.clearError();
    this.renderer.setLoading(true);
    emit(EVENTS.LOADING, { loading: true });

    return this.service
      .changeLine({
        line: line,
        key: key,
        quantity: quantity,
        sections: [SECTION_CART_DRAWER, SECTION_CART_ICON],
        sectionsUrl: global.location.pathname
      })
      .then(function (cart) {
        self.renderer.applyCartResponse(cart);
        if (lifecycle) lifecycle.remount();
        if (typeof global.publish === 'function' && global.PUB_SUB_EVENTS && global.PUB_SUB_EVENTS.cartUpdate) {
          global.publish(global.PUB_SUB_EVENTS.cartUpdate, {
            source: 'basari-cart',
            cartData: cart
          });
        }
        emit(EVENTS.UPDATED, { cart: cart, source: 'change' });
        return cart;
      })
      .catch(function (error) {
        self.renderer.showError(error && error.message);
        emit(EVENTS.ERROR, { error: error });
        return null;
      })
      .then(function (cart) {
        self.renderer.setLoading(false);
        emit(EVENTS.LOADING, { loading: false });
        self._busy = false;
        return cart;
      });
  };

  /* ---------------------------------------------------------------------------
   * Bootstrap
   * ------------------------------------------------------------------------- */

  var activeController = null;
  var lifecycle = null;

  function mount(root) {
    root = root || document.querySelector(ROOT_SELECTOR);
    if (!root) return null;

    if (root.getAttribute(READY_ATTR) === 'true' && activeController && activeController.root === root) {
      return activeController;
    }

    if (activeController) {
      activeController.destroy();
      activeController = null;
    }

    var service = new BasariCartService();
    var renderer = new BasariCartRenderer(root);
    activeController = new BasariCartController(root, service, renderer);
    activeController.init();
    return activeController;
  }

  function initAll() {
    return mount(document.querySelector(ROOT_SELECTOR));
  }

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  }

  onReady(function () {
    lifecycle = new BasariCartLifecycle({
      mount: initAll,
      onOpen: function (drawer) {
        emit(EVENTS.OPENED, { drawer: drawer, source: 'dawn' });
        if (activeController) activeController.refresh();
      },
      onClose: function (drawer) {
        emit(EVENTS.CLOSED, { drawer: drawer, source: 'dawn' });
      }
    });
    lifecycle.start();
  });

  global.BasariCart = {
    Lifecycle: BasariCartLifecycle,
    Controller: BasariCartController,
    Service: BasariCartService,
    Renderer: BasariCartRenderer,
    init: initAll,
    mount: mount,
    getController: function () {
      return activeController;
    },
    EVENTS: EVENTS
  };
})(typeof window !== 'undefined' ? window : this);
